import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import axios from 'axios'
import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ActionButton } from 'shared/components/Button/ActionButton'
import { TextV2 } from 'shared/components/Text/TextV2'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import { switchOrganization } from '../../../helpers/switchOrganization'

export interface Membership {
  id: string
  name: string
  organization: string | null
  is_org_admin: boolean
}

export interface Memberships {
  current: string
  requires_sign_in: boolean
  memberships: Membership[]
}

export function useOrganizationMemberships(userId?: string) {
  return useQuery(
    ['organization-memberships', userId],
    () => axios.get<Memberships>('/api/v1/auth/memberships/').then(({ data }) => data),
    { enabled: Boolean(userId), retry: false }
  )
}

interface OrganizationSwitchDialogProps {
  membership: Membership | null
  currentUserId: string
  onClose: () => void
}

/** Confirms a switch to another organization membership and keeps an uncertain switch covered. */
export function OrganizationSwitchDialog({
  membership,
  currentUserId,
  onClose,
}: OrganizationSwitchDialogProps) {
  const queryClient = useQueryClient()
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState('')

  const confirm = async () => {
    if (!membership || switching) return
    setSwitching(true)
    setError('')
    try {
      await switchOrganization(membership.id, currentUserId, queryClient)
    } catch {
      // A timeout may mean the server switched but its response was lost. Keep private content
      // covered until a fresh navigation reconciles the session rather than assuming the old org.
      setError('Unable to confirm the switch. Reload the app to check your active organization.')
    }
  }

  return (
    <Dialog
      open={Boolean(membership)}
      onClose={switching ? undefined : onClose}
      aria-labelledby="organization-switch-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="organization-switch-title">
        {switching ? 'Switching organization…' : `Switch to ${membership?.name || 'another organization'}?`}
      </DialogTitle>
      <DialogContent>
        <TextV2 type={TYPOGRAPHY.paragraph2}>
          Your access and data will change to this organization. Any unsaved edits will be discarded.
        </TextV2>
        {error && (
          <Alert severity="error" className="mt-3">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions className="px-6 pb-5">
        {error ? (
          <ActionButton onClick={() => window.location.replace('/')}>Reload app</ActionButton>
        ) : (
          <>
            <ActionButton variant="text" isDisabled={switching} onClick={onClose}>
              Cancel
            </ActionButton>
            <ActionButton isDisabled={switching} onClick={confirm}>
              Switch organization
            </ActionButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
